import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import styled, { keyframes } from "styled-components";

// const tg = window.Telegram.WebApp;

// Анимации
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
`;

const shimmer = keyframes`
  0% { background-position: -468px 0; }
  100% { background-position: 468px 0; }
`;

// Стилизованные компоненты
const Container = styled.div`
  max-width: 100%;
  padding: 1rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen",
    "Ubuntu", "Cantarell", sans-serif;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%);
  background-size: 200% 200%;
  animation: gradientShift 8s ease infinite;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 20% 80%,
        rgba(120, 119, 198, 0.3) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 20%,
        rgba(255, 119, 198, 0.3) 0%,
        transparent 50%
      );
    pointer-events: none;
  }

  @keyframes gradientShift {
    0%,
    100% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
  }

  @media (max-width: 768px) {
    padding: 0.5rem;
    min-height: 100vh;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  padding: 2.5rem;
  border-radius: 24px;
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  width: 100%;
  max-width: 600px;
  animation: ${fadeIn} 0.8s ease-out;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    animation: ${shimmer} 3s infinite;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 20px;
    margin: 0 0.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    border-radius: 16px;
  }
`;

const Title = styled.h1`
  color: #333;
  text-align: center;
  margin-bottom: 2rem;
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  line-height: 1.2;

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

// &::before {
//   content: "💡";
//   position: absolute;
//   top: -10px;
//   left: 20px;
//   background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
//   width: 32px;
//   height: 32px;
//   border-radius: 50%;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   font-size: 16px;
//   box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
// }

const Description = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 1.5rem;
  border-radius: 16px;
  margin-bottom: 2rem;
  border: 1px solid rgba(102, 126, 234, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  font-size: 0.95rem;
  line-height: 1.7;
  color: #475569;
  position: relative;

  strong {
    color: #334155;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    padding: 1.25rem;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
  }
`;

const DropzoneContainer = styled.div`
  border: 3px dashed ${(props) => (props.isDragActive ? "#667eea" : "#cbd5e1")};
  border-radius: 20px;
  padding: 3rem 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${(props) =>
    props.isDragActive
      ? "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)"
      : "linear-gradient(135deg, #fafafa 0%, #f1f5f9 100%)"};
  margin-bottom: 1.5rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(255, 255, 255, 0.1) 50%,
      transparent 70%
    );
    transform: translateX(-100%);
    transition: transform 0.6s;
  }

  &:hover {
    border-color: #667eea;
    background: linear-gradient(
      135deg,
      rgba(102, 126, 234, 0.08) 0%,
      rgba(118, 75, 162, 0.08) 100%
    );
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(102, 126, 234, 0.15);

    &::before {
      transform: translateX(100%);
    }
  }

  @media (max-width: 768px) {
    padding: 2rem 1rem;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 0.75rem;
  }
`;

const DropzoneText = styled.div`
  font-size: clamp(1.1rem, 3vw, 1.3rem);
  color: #475569;
  margin-bottom: 0.75rem;
  font-weight: 600;
  animation: ${float} 3s ease-in-out infinite;
`;

const DropzoneSubtext = styled.div`
  font-size: clamp(0.85rem, 2.5vw, 0.95rem);
  color: #64748b;
  opacity: 0.8;
`;

const FileInfo = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 1.5rem;
  border-radius: 16px;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(102, 126, 234, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 0 2px 2px 0;
  }

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const FileName = styled.div`
  font-weight: 700;
  color: #334155;
  margin-bottom: 0.5rem;
  font-size: clamp(0.95rem, 2.5vw, 1.1rem);
  word-break: break-word;
`;

const FileSize = styled.div`
  color: #64748b;
  font-size: clamp(0.85rem, 2vw, 0.95rem);
  font-weight: 500;
`;

const UploadButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 16px;
  cursor: pointer;
  font-size: clamp(1rem, 2.5vw, 1.2rem);
  font-weight: 700;
  width: 100%;
  margin-top: 1.5rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.4);
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.6s;
  }

  &:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.5);

    &::before {
      left: 100%;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    background: linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%);
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

    &::before {
      display: none;
    }
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1.5rem;
    margin-top: 1.25rem;
  }
`;

const ProgressContainer = styled.div`
  margin-top: 1.5rem;
  animation: ${fadeIn} 0.5s ease-out;

  @media (max-width: 768px) {
    margin-top: 1.25rem;
  }
`;

const ProgressText = styled.p`
  text-align: center;
  margin-bottom: 1rem;
  font-weight: 700;
  color: #667eea;
  font-size: clamp(0.95rem, 2.5vw, 1.1rem);
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 12px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 0.75rem;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  background-size: 200% 100%;
  width: ${(props) => props.progress}%;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px;
  animation: shimmerProgress 2s linear infinite;
  box-shadow: 0 0 20px rgba(102, 126, 234, 0.5);

  @keyframes shimmerProgress {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

const ProgressPercentage = styled.p`
  text-align: center;
  margin: 0;
  font-weight: 700;
  color: #667eea;
  font-size: clamp(0.9rem, 2vw, 1rem);
`;

const Result = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  border-radius: 16px;
  animation: ${fadeIn} 0.5s ease-out;
  font-weight: 600;
  font-size: clamp(0.9rem, 2vw, 1rem);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    border-radius: 0 2px 2px 0;
  }

  &.success {
    background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
    color: #166534;
    border: 1px solid #86efac;
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.15);

    &::before {
      background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
    }
  }

  &.error {
    background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
    color: #991b1b;
    border: 1px solid #f87171;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);

    &::before {
      background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    }
  }

  @media (max-width: 768px) {
    padding: 1.25rem;
    margin-top: 1.25rem;
  }
`;

const FileUpload = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const orderId = searchParams.get("order_id");

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [orderError, setOrderError] = useState(null);

  const getUploadInfo = async () => {
    axios
      .get(`https://progressticket.ezbook.uz/web/upload-info/${orderId}`)
      .then((res) => {
        setOrderInfo(res.data);
        setLoadingOrder(false);
      })
      .catch((err) => {
        setOrderError(
          err.response?.data?.message || "Ошибка получения информации о билете"
        );
        setLoadingOrder(false);
      });
  };
  // Получаем информацию о билете при монтировании
  React.useEffect(() => {
    if (!orderId) {
      setOrderError("Не указан order_id в ссылке");
      setLoadingOrder(false);
      return;
    }

    getUploadInfo();
  }, [orderId]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setResult(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    maxSize: 1024 * 1024 * 1024, // 1GB
  });

  const uploadFile = async () => {
    if (!selectedFile) {
      alert("Пожалуйста, выберите файл");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("orderId", orderId);

    setUploading(true);
    setProgress(0);
    setResult(null);

    try {
      const response = await axios.post(
        "https://progressticket.ezbook.uz/web/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setProgress(percentCompleted);
          },
        }
      );

      setResult({
        type: "success",
        message: `Файл "${response.data.fileName}" успешно загружен на Yandex Disk!`,
        size: (response.data.fileSize / 1024 / 1024).toFixed(2),
      });
      setSelectedFile(null);

      getUploadInfo();
    } catch (error) {
      setResult({
        type: "error",
        message:
          error.response?.data?.message ||
          "Произошла ошибка при загрузке файла",
      });
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Container>
      <Card>
        <Title>📁 Загрузка файла</Title>
        {loadingOrder ? (
          <Description>
            <strong>⏳ Загрузка информации о билете...</strong>
          </Description>
        ) : orderError ? (
          <Result className="error">
            <strong>❌ Ошибка:</strong> {orderError}
          </Result>
        ) : orderInfo && orderInfo.success ? (
          orderInfo.can_upload && !orderInfo.is_accepted ? (
            <>
              <Description>
                <strong>🚀 Инструкция:</strong> Перетащите файл в область ниже
                или нажмите для выбора. Максимальный размер файла:{" "}
                <strong>500 мегабайт</strong>. Все ваши файли заархивируйте и
                отправьте zip файл
                <br />
                <h3>Номинация:</h3> {orderInfo.category_text}
              </Description>
              <DropzoneContainer
                {...getRootProps()}
                isDragActive={isDragActive}
              >
                <input {...getInputProps()} />
                {isDragActive ? (
                  <div>
                    <DropzoneText>📤 Отпустите файл здесь</DropzoneText>
                    <DropzoneSubtext>Файл будет загружен</DropzoneSubtext>
                  </div>
                ) : (
                  <div>
                    <DropzoneText>
                      📁 Перетащите файл сюда или нажмите для выбора
                    </DropzoneText>
                    <DropzoneSubtext>
                      Поддерживаются файл до 500 мегабайт
                    </DropzoneSubtext>
                  </div>
                )}
              </DropzoneContainer>
              {selectedFile && (
                <FileInfo>
                  <FileName>📄 {selectedFile.name}</FileName>
                  <FileSize>
                    📊 Размер: {formatFileSize(selectedFile.size)}
                  </FileSize>
                </FileInfo>
              )}
              <UploadButton
                onClick={uploadFile}
                disabled={!selectedFile || uploading}
              >
                {uploading ? "⏳ Загружается..." : "🚀 Загрузить файл"}
              </UploadButton>
              {uploading && (
                <ProgressContainer>
                  <ProgressText>Загрузка файла на Yandex Disk...</ProgressText>
                  <ProgressBar>
                    <ProgressFill progress={progress} />
                  </ProgressBar>
                  <ProgressPercentage>{progress}%</ProgressPercentage>
                </ProgressContainer>
              )}
              {result && (
                <Result className={result.type}>
                  {result.type === "success" ? (
                    <div>
                      <strong>✅ Успешно!</strong>
                      <br />
                      {result.message}
                      <br />
                      {result.size && `Размер: ${result.size} МБ`}
                    </div>
                  ) : (
                    <div>
                      <strong>❌ Ошибка:</strong> {result.message}
                    </div>
                  )}
                </Result>
              )}
            </>
          ) : !orderInfo.can_upload && !orderInfo.is_accepted ? (
            <Result className="success">
              <strong>⏳ Билет на модерации.</strong>
              <br />
              Пожалуйста, дождитесь подтверждения.
              <br />
              Ваш проект в течение 5 рабочих дней пройдёт модерацию.
              <br />
              Если он соответствует всем требованиям, вы получите уведомление о
              включении проекта в конкурс.
              <br />В случае несоответствия, вы получите уведомление с просьбой
              самостоятельно перепроверить соответствие проекта критериям и
              правилам номинации, после чего сможете отправить заявку повторно.
              <br />
              Спасибо, что выбираете IDA!
            </Result>
          ) : !orderInfo.can_upload && orderInfo.is_accepted ? (
            <Result className="success">
              <strong>✅ Билет успешно принят!</strong>
            </Result>
          ) : null
        ) : null}
      </Card>
    </Container>
  );
};

export default FileUpload;
